import { useState } from "react";
import { DesktopLayout } from "../../components/DesktopLayout";
import { useApp } from "../../context/AppContext";
import { formatDataHora } from "../../data/mock";
import { StatusBadge } from "../../components/StatusBadge";
import {
  Users,
  ShieldCheck,
  Mail,
  Clock,
  MapPin,
  BadgeCheck,
  Eye,
  X,
  Phone,
  AlertTriangle,
  Footprints,
  UserCheck,
  UserX,
  Search,
} from "lucide-react";

const card = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
};

export default function VigilantesDesktop() {
  const { vigilantes, postos, condominios, rondas, ocorrencias, getById } = useApp();

  const [vigilanteSelecionado, setVigilanteSelecionado] = useState(null);
  const [busca, setBusca] = useState("");

  const totalVigilantes = vigilantes.length;
  const emServico       = vigilantes.filter((v) => v.emServico).length;
  const foraServico     = vigilantes.filter((v) => !v.emServico).length;
  const cobertura       = totalVigilantes > 0 ? Math.round((emServico / totalVigilantes) * 100) : 0;

  const getUltimaRonda = (vigilanteId) =>
    rondas
      .filter((r) => r.vigilanteId === vigilanteId)
      .sort((a, b) => new Date(b.fim || b.inicio) - new Date(a.fim || a.inicio))[0];

  const getOcorrenciasVigilante = (vigilanteId) =>
    ocorrencias.filter((o) => o.vigilanteId === vigilanteId);

  const filtrados = vigilantes.filter((v) =>
    !busca || v.nome.toLowerCase().includes(busca.toLowerCase()),
  );

  return (
    <DesktopLayout
      title="Vigilantes"
      subtitle="Equipe cadastrada, status de plantão e visão operacional"
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Users}     label="Cadastrados"  value={totalVigilantes} color="blue"    />
        <KpiCard icon={UserCheck} label="Em serviço"   value={emServico}       color="emerald" />
        <KpiCard icon={UserX}     label="Fora"         value={foraServico}     color="amber"   />
        <KpiCard icon={ShieldCheck} label="Cobertura"  value={`${cobertura}%`} color="violet"  />
      </div>

      {/* Busca */}
      <div className="rounded-2xl p-4 mb-6 flex items-center gap-3" style={card}>
        <Search className="w-4 h-4 flex-shrink-0" style={{ color: "rgba(255,255,255,0.3)" }} />
        <input
          type="text"
          placeholder="Buscar vigilante pelo nome…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="flex-1 bg-transparent outline-none text-[14px]"
          style={{ color: "rgba(255,255,255,0.8)", caretColor: "#3B82F6" }}
        />
        {busca && (
          <button onClick={() => setBusca("")} style={{ color: "rgba(255,255,255,0.3)" }}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tabela */}
      <div className="rounded-2xl overflow-hidden" style={card}>
        {/* Header da tabela */}
        <div
          className="grid px-5 py-3"
          style={{
            gridTemplateColumns: "1fr 120px 160px 80px 100px 44px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          {["Vigilante", "Status", "Posto", "Ocorr.", "Última ronda", ""].map((h) => (
            <span
              key={h}
              className="text-[10px] font-bold tracking-[0.1em] uppercase"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              {h}
            </span>
          ))}
        </div>

        {/* Linhas */}
        {filtrados.map((vigilante) => {
          const posto              = getById(postos, vigilante.postoId);
          const condominio         = getById(condominios, vigilante.condominioId);
          const ultimaRonda        = getUltimaRonda(vigilante.id);
          const ocorrenciasVigilante = getOcorrenciasVigilante(vigilante.id);
          const iniciais           = vigilante.nome.split(" ").map((n) => n[0]).slice(0, 2).join("");

          return (
            <div
              key={vigilante.id}
              data-testid={`vig-row-${vigilante.id}`}
              className="grid px-5 py-4 items-center transition"
              style={{
                gridTemplateColumns: "1fr 120px 160px 80px 100px 44px",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.02)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {/* Nome + avatar */}
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center text-[11px] font-bold"
                  style={{ background: "rgba(59,130,246,0.12)", color: "#3B82F6" }}
                >
                  {iniciais}
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-white/90 truncate">{vigilante.nome}</p>
                  <p className="text-[11px] font-mono" style={{ color: "rgba(255,255,255,0.3)" }}>{vigilante.matricula}</p>
                </div>
              </div>

              {/* Status */}
              <div>
                {vigilante.emServico ? (
                  <StatusBadge tone="success">em serviço</StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">fora</StatusBadge>
                )}
              </div>

              {/* Posto */}
              <p className="text-[13px] truncate" style={{ color: "rgba(255,255,255,0.5)" }}>
                {posto?.nome || "—"}
              </p>

              {/* Ocorrências */}
              <p
                className="text-[13px] font-mono font-semibold"
                style={{ color: ocorrenciasVigilante.length > 0 ? "#F59E0B" : "rgba(255,255,255,0.3)" }}
              >
                {ocorrenciasVigilante.length}
              </p>

              {/* Última ronda */}
              <p className="text-[12px] font-mono" style={{ color: "rgba(255,255,255,0.3)" }}>
                {ultimaRonda ? formatDataHora(ultimaRonda.fim || ultimaRonda.inicio) : "—"}
              </p>

              {/* Ação */}
              <button
                type="button"
                onClick={() => setVigilanteSelecionado({ ...vigilante, posto, condominio, ultimaRonda, ocorrenciasVigilante })}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.9)"; e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.5)"; e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}

        {filtrados.length === 0 && (
          <div className="px-5 py-12 text-center">
            <p className="text-[14px]" style={{ color: "rgba(255,255,255,0.25)" }}>
              {busca ? `Nenhum vigilante encontrado para "${busca}".` : "Nenhum vigilante cadastrado."}
            </p>
          </div>
        )}
      </div>

      {vigilanteSelecionado && (
        <PerfilVigilanteModal
          vigilante={vigilanteSelecionado}
          onClose={() => setVigilanteSelecionado(null)}
        />
      )}
    </DesktopLayout>
  );
}

const KpiCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue:   { bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.15)",  text: "#3B82F6" },
    emerald:{ bg: "rgba(16,185,129,0.08)",  border: "rgba(16,185,129,0.15)",  text: "#10B981" },
    amber:  { bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.15)",  text: "#F59E0B" },
    violet: { bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.15)",  text: "#8B5CF6" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="rounded-2xl p-5" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-3" style={{ color: `${c.text}80` }}>{label}</p>
          <p className="text-[30px] font-bold leading-none" style={{ color: c.text }}>{value}</p>
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${c.text}18` }}>
          <Icon className="w-4.5 h-4.5" style={{ color: c.text }} />
        </div>
      </div>
    </div>
  );
};

const PerfilVigilanteModal = ({ vigilante, onClose }) => (
  <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
    <div
      className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
      style={{ background: "#08111F", border: "1px solid rgba(255,255,255,0.1)" }}
    >
      <div className="px-6 py-5 flex items-start justify-between gap-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-[16px] flex-shrink-0"
            style={{ background: "rgba(59,130,246,0.12)", color: "#3B82F6", border: "1px solid rgba(59,130,246,0.2)" }}
          >
            {vigilante.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>
              Perfil operacional
            </p>
            <h2 className="text-[18px] font-bold text-white">{vigilante.nome}</h2>
            <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
              {vigilante.matricula} · {vigilante.posto?.nome || "—"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.5)" }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-3">
        <InfoBox icon={Mail}        label="E-mail"        value={vigilante.email} />
        <InfoBox icon={Phone}       label="Contato"       value="Não informado" />
        <InfoBox icon={MapPin}      label="Posto"         value={vigilante.posto?.nome || "Não informado"} />
        <InfoBox icon={ShieldCheck} label="Condomínio"    value={vigilante.condominio?.nome || "Não informado"} />
        <InfoBox icon={Clock}       label="Plantão"       value={vigilante.inicioPlantao ? formatDataHora(vigilante.inicioPlantao) : "Sem plantão ativo"} />
        <InfoBox icon={BadgeCheck}  label="Status"        value={vigilante.emServico ? "Em serviço" : "Fora de serviço"} />
        <InfoBox icon={Footprints}  label="Última ronda"  value={vigilante.ultimaRonda ? formatDataHora(vigilante.ultimaRonda.fim || vigilante.ultimaRonda.inicio) : "Nenhuma ronda"} />
        <InfoBox icon={AlertTriangle} label="Ocorrências" value={`${vigilante.ocorrenciasVigilante?.length || 0} registrada(s)`} />
      </div>

      <div className="px-6 pb-5">
        <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.3)" }}>
            Perfil demonstrativo. Na versão real, esta tela exibirá documentos, telefone, escala, histórico completo de rondas e registros de ponto.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const InfoBox = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl p-3.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#3B82F6" }} />
      <div>
        <p className="text-[10px] font-semibold mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>{label}</p>
        <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.8)" }}>{value}</p>
      </div>
    </div>
  </div>
);
